import { distance } from "@turf/turf";
import { ContractList } from "../../models/contract/ContractList";

/**
 * Row model for the Today Delivery gate view, built from the one
 * `ne_contract_list` query.
 *
 * Positions are the awkward part. A delivery has no single coordinate, so one
 * is resolved down POSITION_CHAIN: the truck photo first, then the Unilever
 * plot, then the supplier's registered point, then nothing. Which rung answered
 * is kept on the row, because "where the truck was" and "where the agent is
 * registered" are very different claims to put in front of a mill.
 */

export type DeliveryStatus = 'open' | 'transit' | 'delivered' | 'paused' | 'other';

/** Which rung of the fallback produced the row's coordinates. */
export type CoordinateSource = 'truck_photo' | 'supplier' | 'unilever';

/** Ordered best-first; the first rung with a position wins. */
const POSITION_CHAIN: CoordinateSource[] = ['truck_photo', 'unilever', 'supplier'];

/** Label key for each rung, so the UI can say where a coordinate came from. */
export const COORDINATE_SOURCE_LABEL: Record<CoordinateSource, string> = {
    truck_photo: 'position_from_truck_photo',
    supplier: 'position_from_supplier',
    unilever: 'position_from_unilever',
};

export type DeliveryRow = {
    contractId: number
    /** Raw backend status, kept so an unmapped value can still be shown. */
    status: string
    statusKind: DeliveryStatus
    /** Translation key for the status pill, or undefined when the raw value is unmapped. */
    statusLabelKey?: string
    isNac: boolean
    statementId?: number
    deliveryDate?: string
    quantity?: number
    pricePerKg?: number

    supplierName?: string
    village?: string
    /** Sub-district → district → province, coarsest last, blanks dropped. */
    area?: string
    coordinates?: [number, number]
    /** Which rung of POSITION_CHAIN the coordinates came from. */
    coordinateSource?: CoordinateSource
    distanceKm?: number
    /** Firebase URI of the photo shown in the row, truck shot or weight receipt. */
    photoUri?: string
    photoKind?: 'truck' | 'receipt'
    /** Download URL of the truck photo, once resolved from storage; drives the list thumbnail. */
    photoUrl?: string
}

/**
 * Statuses `ne_contract_list` actually returns. Everything is prefixed OPEN_
 * because the contract stays open until it is invoiced, so the suffix is the
 * part that matters at the gate.
 */
const STATUS_MAP: Record<string, { kind: DeliveryStatus, labelKey: string }> = {
    OPEN: { kind: 'open', labelKey: 'status_open' },
    OPEN_PICKED_UP: { kind: 'transit', labelKey: 'status_picked_up' },
    OPEN_DELIVERED: { kind: 'delivered', labelKey: 'status_delivered' },
    PAUSED: { kind: 'paused', labelKey: 'status_paused' },
};

export const statusKind = (status?: string): DeliveryStatus =>
    STATUS_MAP[(status ?? '').toUpperCase()]?.kind ?? 'other';

export const statusLabelKey = (status?: string): string | undefined =>
    STATUS_MAP[(status ?? '').toUpperCase()]?.labelKey;

/**
 * ne_contract_list still returns cancelled contracts; no truck is coming for
 * them, so they are dropped before anything is shown or counted.
 */
export const isCancelled = (status?: string): boolean =>
    (status ?? '').toUpperCase() === 'CANCELLED';

/** Open and picked-up are both still expected through the gate. */
export const isPending = (kind: DeliveryStatus): boolean =>
    kind === 'open' || kind === 'transit';

export const statusPillClass = (kind: DeliveryStatus): string => {
    switch (kind) {
        case 'open': return 'ds-pill--state-open';
        case 'transit': return 'ds-pill--state-transit';
        case 'delivered': return 'ds-pill--state-done';
        case 'paused': return 'ds-pill--state-paused';
        default: return 'ds-pill--state-other';
    }
};

const isValidCoordinate = (coords: any): coords is [number, number] =>
    Array.isArray(coords) && coords.length === 2 && coords.every(c => Number.isFinite(Number(c)));

const trimmed = (value?: string): string | undefined => {
    const text = value?.trim();
    return text === '' ? undefined : text;
};

const asCoordinates = (raw: any): [number, number] | undefined =>
    isValidCoordinate(raw) ? [Number(raw[0]), Number(raw[1])] : undefined;

/** Walks POSITION_CHAIN and returns the first rung that has a position. */
const resolvePosition = (
    candidates: Partial<Record<CoordinateSource, [number, number] | undefined>>
): { coordinates?: [number, number], coordinateSource?: CoordinateSource } => {
    for (const source of POSITION_CHAIN) {
        const coordinates = candidates[source];
        if (coordinates) return { coordinates, coordinateSource: source };
    }
    return {};
};

export const kmBetween = (
    from: [number, number] | undefined,
    to: [number, number] | undefined
): number | undefined =>
    from && to ? distance(from, to, { units: 'kilometers' }) : undefined;

export const buildDeliveryRows = (
    contracts: ContractList[],
    millCoords?: [number, number]
): DeliveryRow[] =>
    (contracts ?? []).filter(contract => !isCancelled(contract.status)).map(contract => {
        const position = resolvePosition({
            truck_photo: asCoordinates(contract.truck_photo_coordinates),
            supplier: asCoordinates(contract.supplier_coordinates),
            unilever: asCoordinates(contract.unilever_coordinates),
        });

        // Coarsest last, and a missing level just drops out of the trail.
        const area = [contract.subdistrict, contract.district, contract.province]
            .map(trimmed)
            .filter(Boolean)
            .join(', ');

        // The truck shot is the subject of the column; the receipt stands in
        // when the contract has none.
        const truckPhoto = trimmed(contract.truck_photo_uri);
        const receipt = trimmed(contract.weight_receipt_uri);

        return {
            contractId: contract.id,
            status: contract.status ?? '',
            statusKind: statusKind(contract.status),
            statusLabelKey: statusLabelKey(contract.status),
            isNac: contract.isNac(),
            statementId: contract.statement?.id,
            deliveryDate: contract.getDeliveryDate(),
            quantity: contract.getQuantity(),
            pricePerKg: contract.getPricePerUnit(),
            supplierName: trimmed(contract.supplier_name),
            village: trimmed(contract.village),
            area: area === '' ? undefined : area,
            ...position,
            distanceKm: kmBetween(millCoords, position.coordinates),
            photoUri: truckPhoto ?? receipt,
            photoKind: truckPhoto ? 'truck' : receipt ? 'receipt' : undefined,
        };
    });

/**
 * Fills the truck-photo rung from the storage object when the API did not carry
 * a position on the photo record itself. Runs after `buildDeliveryRows` because
 * the metadata resolves long after the contracts land.
 *
 * Only a truck photo counts: a weight receipt is shot at the mill gate and
 * carries no GPS, so it would answer with the mill's own position at best.
 */
export const applyPhotoPositions = (
    rows: DeliveryRow[],
    photoCoordinates: (uri: string) => [number, number] | undefined,
    millCoords?: [number, number]
): DeliveryRow[] =>
    (rows ?? []).map(row => {
        if (row.coordinateSource === 'truck_photo' || row.photoKind !== 'truck') return row;

        const fromPhoto = row.photoUri ? photoCoordinates(row.photoUri) : undefined;
        if (!fromPhoto) return row;

        return {
            ...row,
            coordinates: fromPhoto,
            coordinateSource: 'truck_photo',
            distanceKm: kmBetween(millCoords, fromPhoto),
        };
    });

/** Truck photo URIs of the rows, for resolving thumbnails and positions in one pass. */
export const truckPhotoUris = (rows: DeliveryRow[]): string[] =>
    (rows ?? []).filter(row => row.photoKind === 'truck' && row.photoUri).map(row => row.photoUri);

/** Attaches each truck photo's resolved download URL, so the list can show a thumbnail. */
export const applyPhotoUrls = (
    rows: DeliveryRow[],
    photoUrl: (uri: string) => string | undefined
): DeliveryRow[] =>
    (rows ?? []).map(row => {
        const url = row.photoKind === 'truck' && row.photoUri ? photoUrl(row.photoUri) : undefined;
        return url ? { ...row, photoUrl: url } : row;
    });

export type DeliveryKpis = {
    contractCount: number
    openCount: number
    avgDistanceKm: number | undefined
}

export const computeDeliveryKpis = (rows: DeliveryRow[]): DeliveryKpis => {
    const measured = (rows ?? []).filter(r => r.distanceKm !== undefined);
    return {
        contractCount: rows?.length ?? 0,
        openCount: (rows ?? []).filter(r => isPending(r.statusKind)).length,
        avgDistanceKm: measured.length > 0
            ? measured.reduce((sum, r) => sum + r.distanceKm, 0) / measured.length
            : undefined,
    };
};

/** `lat, lng` at the precision the gate log uses. */
export const formatLatLng = (coords: [number, number]): string =>
    `${coords[1].toFixed(4)}, ${coords[0].toFixed(4)}`;
